
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { getCourseById } from "@/services/courseManagementService";
import { 
  createLesson,
  updateLesson,
  deleteLesson,
  reorderLessons
} from "@/services/lessonManagement.service";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, ChevronRight } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CourseModule, CourseLesson } from "@/types/course";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
  BreadcrumbPage
} from "@/components/ui/breadcrumb";

const ModuleLessonsPage: React.FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  
  const [module, setModule] = useState<CourseModule | null>(null);
  const [courseId, setCourseId] = useState<string | null>(null);
  const [courseTitle, setCourseTitle] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentLesson, setCurrentLesson] = useState<{
    id?: string;
    title: string;
    description: string;
    video_url: string;
    duration: string;
  }>({
    title: "",
    description: "",
    video_url: "",
    duration: "",
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState<string | null>(null);

  useEffect(() => {
    if (moduleId) {
      fetchModule();
    }
  }, [moduleId]);

  const fetchModule = async () => {
    setIsLoading(true);
    try {
      if (!moduleId) return;
      
      // First, fetch the module
      const { data: moduleData, error: moduleError } = await supabase
        .from('course_modules')
        .select('*, course_id')
        .eq('id', moduleId)
        .single();
      
      if (moduleError) throw moduleError;
      
      // Set the course ID for back navigation
      if (moduleData) {
        setCourseId(moduleData.course_id);
        // Fetch course title
        if (moduleData.course_id) {
          const course = await getCourseById(moduleData.course_id);
          if (course) {
            setCourseTitle(course.title);
          }
        }
      }
      
      // Then fetch the lessons for this module
      const { data: lessonsData, error: lessonsError } = await supabase
        .from('course_lessons')
        .select('*')
        .eq('module_id', moduleId)
        .order('order_index');
      
      if (lessonsError) throw lessonsError;
      
      // Combine the data
      const completeModule: CourseModule = {
        ...moduleData,
        lessons: lessonsData.map((lesson) => ({
          ...lesson,
          completed: false,
          last_position: 0
        })),
        completedLessons: 0,
        totalLessons: lessonsData.length
      };
      
      setModule(completeModule);
    } catch (error) {
      console.error("Error fetching module:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch module data. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateLesson = async () => {
    try {
      if (!moduleId || !currentLesson.title.trim()) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Lesson title is required.",
        });
        return;
      }

      const newOrderIndex = module?.lessons?.length 
        ? Math.max(...module.lessons.map(l => l.order_index)) + 1 
        : 0;
      
      const durationValue = currentLesson.duration 
        ? parseInt(currentLesson.duration, 10) 
        : null;
      
      await createLesson(
        moduleId,
        currentLesson.title,
        currentLesson.description || null,
        currentLesson.video_url || null,
        newOrderIndex,
        durationValue
      );
      
      toast({
        title: "Success",
        description: "Lesson created successfully.",
      });
      
      setIsCreating(false);
      setCurrentLesson({ title: "", description: "", video_url: "", duration: "" });
      fetchModule();
    } catch (error) {
      console.error("Error creating lesson:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create lesson. Please try again.",
      });
    }
  };

  const handleEditLesson = async () => {
    try {
      if (!currentLesson.id || !currentLesson.title.trim()) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Lesson title is required.",
        });
        return;
      }

      const durationValue = currentLesson.duration 
        ? parseInt(currentLesson.duration, 10) 
        : null;
      
      await updateLesson(currentLesson.id, {
        title: currentLesson.title,
        description: currentLesson.description || null,
        video_url: currentLesson.video_url || null,
        duration: durationValue
      });
      
      toast({
        title: "Success",
        description: "Lesson updated successfully.",
      });
      
      setIsEditing(false);
      setCurrentLesson({ title: "", description: "", video_url: "", duration: "" });
      fetchModule();
    } catch (error) {
      console.error("Error updating lesson:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update lesson. Please try again.",
      });
    }
  };

  const handleDeleteLesson = async () => {
    try {
      if (!lessonToDelete) return;

      await deleteLesson(lessonToDelete);
      
      toast({
        title: "Success",
        description: "Lesson deleted successfully.",
      });
      
      setIsDeleteDialogOpen(false);
      setLessonToDelete(null);
      fetchModule();
    } catch (error) {
      console.error("Error deleting lesson:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete lesson. Please try again.",
      });
    }
  };

  const handleMoveLesson = async (lessonId: string, direction: "up" | "down") => {
    if (!module) return;
    
    const lessons = [...module.lessons];
    const lessonIndex = lessons.findIndex(l => l.id === lessonId);
    
    if (
      (direction === "up" && lessonIndex === 0) || 
      (direction === "down" && lessonIndex === lessons.length - 1)
    ) {
      return;
    }

    const targetIndex = direction === "up" ? lessonIndex - 1 : lessonIndex + 1;
    
    // Swap lessons
    [lessons[lessonIndex], lessons[targetIndex]] = [
      lessons[targetIndex],
      lessons[lessonIndex],
    ];
    
    try {
      if (!moduleId) return;
      await reorderLessons(moduleId, lessons.map(l => l.id));
      
      // Update local state
      setModule({
        ...module,
        lessons: lessons
      });
      
      toast({
        title: "Success",
        description: "Lesson order updated.",
      });
    } catch (error) {
      console.error("Error reordering lessons:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to reorder lessons. Please try again.",
      });
    }
  };

  const formatDuration = (seconds: number | null | undefined): string => {
    if (!seconds) return "-";
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <AdminLayout
      title={module ? `Module: ${module.title}` : "Module Lessons"}
      backLink={courseId ? `/admin/courses/modules/${courseId}` : "/admin/courses"}
    >
      <div className="mb-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/courses">Courses</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            {courseId && courseTitle && (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink href={`/admin/courses/modules/${courseId}`}>{courseTitle}</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </>
            )}
            <BreadcrumbItem>
              <BreadcrumbPage>{module?.title || "Module"}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Lessons</CardTitle>
          <CardDescription>
            Manage lessons for this module. Click on a lesson to view and manage its content.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Button onClick={() => {
              setCurrentLesson({ title: "", description: "", video_url: "", duration: "" });
              setIsCreating(true);
            }}>
              <Plus className="mr-2 h-4 w-4" /> Add Lesson
            </Button>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center py-8">
              <p>Loading lessons...</p>
            </div>
          ) : !module?.lessons || module.lessons.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No lessons found. Create your first lesson to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Order</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-[100px]">Duration</TableHead>
                  <TableHead className="w-[200px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {module.lessons.map((lesson) => (
                  <TableRow 
                    key={lesson.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/admin/courses/lessons/${lesson.id}/content`)}
                  >
                    <TableCell className="font-medium">{lesson.order_index + 1}</TableCell>
                    <TableCell>{lesson.title}</TableCell>
                    <TableCell>{lesson.description || "-"}</TableCell>
                    <TableCell>{formatDuration(lesson.duration)}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentLesson({
                              id: lesson.id,
                              title: lesson.title,
                              description: lesson.description || "",
                              video_url: lesson.video_url || "",
                              duration: lesson.duration ? lesson.duration.toString() : "",
                            });
                            setIsEditing(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setLessonToDelete(lesson.id);
                            setIsDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          disabled={module.lessons.indexOf(lesson) === 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveLesson(lesson.id, "up");
                          }}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          disabled={module.lessons.indexOf(lesson) === module.lessons.length - 1}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveLesson(lesson.id, "down");
                          }}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/courses/lessons/${lesson.id}/content`);
                          }}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Lesson Dialog */}
      <Dialog open={isCreating} onOpenChange={setIsCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Lesson</DialogTitle>
            <DialogDescription>
              Create a new lesson for this module.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="Enter lesson title"
                value={currentLesson.title}
                onChange={(e) => 
                  setCurrentLesson({ ...currentLesson, title: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter lesson description"
                value={currentLesson.description}
                onChange={(e) => 
                  setCurrentLesson({ ...currentLesson, description: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="video_url">Video URL (optional)</Label>
              <Input
                id="video_url"
                placeholder="Enter video URL"
                value={currentLesson.video_url}
                onChange={(e) => 
                  setCurrentLesson({ ...currentLesson, video_url: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="duration">Duration in seconds (optional)</Label>
              <Input
                id="duration"
                type="number"
                placeholder="Enter duration in seconds"
                value={currentLesson.duration}
                onChange={(e) => 
                  setCurrentLesson({ ...currentLesson, duration: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreating(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateLesson}>Create Lesson</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Lesson Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Lesson</DialogTitle>
            <DialogDescription>
              Update lesson details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                placeholder="Enter lesson title"
                value={currentLesson.title}
                onChange={(e) => 
                  setCurrentLesson({ ...currentLesson, title: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                placeholder="Enter lesson description"
                value={currentLesson.description}
                onChange={(e) => 
                  setCurrentLesson({ ...currentLesson, description: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-video_url">Video URL (optional)</Label>
              <Input
                id="edit-video_url"
                placeholder="Enter video URL"
                value={currentLesson.video_url}
                onChange={(e) => 
                  setCurrentLesson({ ...currentLesson, video_url: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-duration">Duration in seconds (optional)</Label>
              <Input
                id="edit-duration"
                type="number"
                placeholder="Enter duration in seconds"
                value={currentLesson.duration}
                onChange={(e) => 
                  setCurrentLesson({ ...currentLesson, duration: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditLesson}>Update Lesson</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this lesson? This action cannot be undone.
                All content associated with this lesson will also be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteLesson}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ModuleLessonsPage;
